import type { LandingPageContent } from '../types'

export const KOSZT_INSTALACJI_FOTOWOLTAICZNEJ: LandingPageContent = {
  slug: 'koszt-instalacji-fotowoltaicznej',
  lang: 'pl',
  topicId: 'solaranlage-kosten',
  title: 'Koszt instalacji fotowoltaicznej – bezpłatne porównanie',
  metaDescription:
    'Ile kosztuje instalacja fotowoltaiczna? Poznaj czynniki wpływające na cenę i porównaj oferty ' +
    'firm rzemieślniczych bezpłatnie.',
  keywords: [
    'koszt instalacji fotowoltaicznej',
    'cena paneli słonecznych',
    'ile kosztuje instalacja fotowoltaiczna',
    'cena instalacji PV',
    'kalkulator kosztów fotowoltaiki',
    'klimatyzacja fotowoltaika',
    'zużycie prądu komputer do gier fotowoltaika',
  ],
  eyebrow: 'Przegląd kosztów',
  h1: 'Ile kosztuje instalacja fotowoltaiczna? Ceny i kluczowe czynniki',
  intro:
    'Koszt instalacji fotowoltaicznej mocno się różni - w zależności od wielkości dachu, mocy ' +
    'instalacji, magazynu energii i regionu różnica między dwiema ofertami dla tego samego dachu ' +
    'może wynosić kilka tysięcy euro. Dowiesz się tutaj, jakie czynniki naprawdę wpływają na cenę i ' +
    'jak znaleźć uczciwą cenę rynkową dla swojego projektu.',
  sections: [
    { type: 'heading', text: 'Dlaczego jedna uniwersalna cena niewiele mówi' },
    {
      type: 'paragraph',
      text:
        'Stała cena za kWp brzmi prosto, ale pomija kształt dachu, potrzebne rusztowanie, wybór ' +
        'magazynu energii i lokalne ceny firm rzemieślniczych. Całkowity koszt wynika dopiero z ' +
        'połączenia kilku czynników - dlatego oferty dla tego samego dachu w praktyce często się ' +
        'znacznie różnią.',
    },
    { type: 'heading', text: 'Te czynniki wpływają na cenę' },
    {
      type: 'list',
      items: [
        'Wielkość instalacji (kWp) i dostępna powierzchnia dachu',
        'Orientacja i kąt nachylenia dachu',
        'Magazyn energii tak/nie i jego pojemność',
        'Dodatkowe zapotrzebowanie z powodu pompy ciepła lub samochodu elektrycznego',
        'Stan rozdzielnicy i potrzebne prace elektryczne',
        'Nakład na rusztowanie i montaż w zależności od typu dachu',
        'Regionalne różnice cenowe między firmami rzemieślniczymi',
      ],
    },
    { type: 'heading', text: 'Dlaczego oferty dla tego samego dachu mogą się tak różnić' },
    {
      type: 'paragraph',
      text:
        'Obłożenie zleceniami, marża i doświadczenie różnią się w zależności od firmy - kto pobiera ' +
        'tylko jedną ofertę, nie ma punktu odniesienia i trudno mu ocenić, czy cena jest uczciwa. ' +
        'Dopiero kilka ofert na to samo zapytanie pokazuje, gdzie naprawdę leży cena rynkowa.',
    },
    { type: 'heading', text: 'Tak znajdziesz najlepszą cenę - bez telefonów od handlowców' },
    {
      type: 'paragraph',
      text:
        'W Roofora tworzysz anonimowe zapytanie: wystarczą zdjęcia dachu, zdjęcie rozdzielnicy, ' +
        'orientacja i Twoje zużycie energii. Firmy rzemieślnicze odpowiadają niewiążącymi ofertami w ' +
        'stałej cenie, które spokojnie porównujesz posortowane według ceny. Kontakt nawiązywany jest ' +
        'dopiero wtedy, gdy aktywnie zgodzisz się na konkretną firmę - bez zbiorowej zgody, bez ' +
        'lawiny telefonów.',
    },
  ],
  faq: [
    {
      q: 'Czy magazyn energii opłaca się cenowo?',
      a: 'Zależy to mocno od Twojego profilu zużycia - na przykład czy zużywasz dużo prądu w ciągu dnia, czy raczej wieczorem. Firmy rzemieślnicze mogą to realistycznie ocenić na podstawie Twoich danych o zużyciu, pompie ciepła i samochodzie elektrycznym.',
    },
    {
      q: 'Dlaczego oferty dla tego samego dachu tak bardzo się różnią?',
      a: 'Ponieważ obłożenie zleceniami, wybór materiałów i kalkulacja różnią się w zależności od firmy. Właśnie dlatego warto porównać kilka niezależnych ofert zamiast tylko jednej.',
    },
    {
      q: 'Czy korzystanie z Roofora coś kosztuje?',
      a: 'Nie - złożenie zapytania, otrzymanie ofert i ich porównanie jest dla Ciebie jako klienta całkowicie bezpłatne.',
    },
    {
      q: 'Czy moja instalacja wystarczy też na klimatyzację latem?',
      a: 'Klimatyzacja pracuje zwykle w ciągu dnia - dokładnie wtedy, gdy instalacja fotowoltaiczna produkuje najwięcej prądu. Zewnętrzne źródła wskazują to jako jedno z ekonomicznie najbardziej opłacalnych połączeń. Potrzebna wielkość zależy od typu urządzenia i pozostałego zużycia - podaj dodatkowe zapotrzebowanie w kalkulatorze doboru instalacji.',
    },
    {
      q: 'Czy komputer do gier zmienia coś w zalecanej wielkości instalacji?',
      a: 'Komputer do gier może pobierać kilkaset watów w zależności od podzespołów, co przy długim użytkowaniu odczuwalnie zwiększa roczne zużycie. Roofora nie liczy dla tego osobnej dopłaty - podaj rzeczywiste roczne zużycie, wliczając granie, w kalkulatorze doboru instalacji, aby wielkość instalacji do niego pasowała.',
    },
    {
      q: 'Czy mogę wykorzystać nadwyżkę fotowoltaiczną do kopania kryptowalut?',
      a: 'Technicznie tak - nadwyżkę prądu słonecznego, która inaczej trafiłaby do sieci za niewielkie pieniądze, można zamiast tego wykorzystać do sprzętu do kopania. To jednak szczególny przypadek bez własnego obliczenia w naszym kalkulatorze - opłacalność mocno zależy od Twojego sprzętu i aktualnych warunków i powinna być liczona osobno.',
    },
  ],
  primaryCta: { label: 'Poproś teraz o bezpłatną ofertę', action: 'customer' },
  secondaryCta: {
    label: 'Jak działa porównanie ofert',
    action: 'link',
    href: '/porownaj-oferty-fotowoltaiczne',
  },
  relatedLinks: [
    { label: 'Jak działa porównanie ofert', href: '/porownaj-oferty-fotowoltaiczne' },
    { label: 'Znajdź instalatora fotowoltaiki', href: '/znajdz-instalatora-fotowoltaiki' },
    { label: 'Zaplanuj instalację krok po kroku', href: '/zaplanuj-instalacje-fotowoltaiczna' },
    { label: 'Fotowoltaika i pompa ciepła razem', href: '/fotowoltaika-pompa-ciepla' },
    { label: 'Wallbox i samochód elektryczny z nadwyżką solarną', href: '/fotowoltaika-wallbox-samochod-elektryczny' },
    { label: 'Dofinansowanie fotowoltaiki: KfW, taryfa i dotacje', href: '/dofinansowanie-fotowoltaiki' },
  ],
}
